// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { Database } from '@/types/database';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Use environment variables without hardcoded fallbacks
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// Debug environment variables in development
if (import.meta.env.DEV) {
  
}

// Track Supabase client initialization
let isInitialized = false;
let isInitializing = false;
let initializationPromise: Promise<void> | null = null;

// Check for missing environment variables
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  
  
  // Show user-friendly error immediately
  if (typeof window !== 'undefined') {
    const errorMsg = 'App configuration error. Please check environment variables.';
    
    
    // Try to show error to user if possible
    setTimeout(() => {
      if (document.body) {
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = `
          position: fixed; top: 0; left: 0; right: 0; 
          background: #dc2626; color: white; padding: 10px; 
          text-align: center; z-index: 10000;
          font-family: system-ui;
        `;
        errorDiv.textContent = 'Configuration Error: Please check app setup';
        document.body.appendChild(errorDiv);
      }
    }, 100);
  }
  
  throw new Error(`Missing Supabase environment variables: URL=${!!SUPABASE_URL}, KEY=${!!SUPABASE_PUBLISHABLE_KEY}`);
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Add a new retry function that will handle network retries
const retryFetch = async (fn: () => Promise<any>, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryFetch(fn, retries - 1, delay * 1.5);
  }
};

// Custom fetch implementation that uses native fetch for all requests
const customFetch = async (url: string, options: any = {}) => {
  try {
    // Always use browser fetch (native or web)
    return await retryFetch(async () => {
      const timeout = 30000; // 30 seconds timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          keepalive: true,
          cache: 'no-store',
          credentials: 'same-origin',
        });
        
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }, 3, 1000);
  } catch (error) {
    throw error;
  }
};

// Create a Capacitor-compatible storage adapter for Supabase
const createCapacitorStorageAdapter = () => {
  return {
    getItem: async (key: string): Promise<string | null> => {
      const { value } = await Preferences.get({ key });
      return value;
    },
    setItem: async (key: string, value: string): Promise<void> => {
      await Preferences.set({ key, value });
    },
    removeItem: async (key: string): Promise<void> => {
      await Preferences.remove({ key });
    }
  };
};

// Create Supabase client with improved configuration for mobile devices
const createSupabaseClient = () => {
  if (isInitializing) {
    
    return null;
  }
  
  isInitializing = true;
  
  
  // Use localStorage for all platforms for better performance
  // The previous Capacitor Preferences approach was causing slow connections
  const storageConfig = {};
  
  try {
    const client = createClient<Database>(
      SUPABASE_URL,
      SUPABASE_PUBLISHABLE_KEY,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: !Capacitor.isNativePlatform(), // Only use in web
          storageKey: 'santiyex-auth-token',
          flowType: 'pkce',
          debug: import.meta.env.DEV, // Only enable debug in development
          ...storageConfig
        },
        global: {
          headers: {
            'X-Client-Info': 'santiyex-mobile-app'
          },
          fetch: customFetch
        }
      }
    );
    
    // Add auth state listener to handle token refresh failures
    client.auth.onAuthStateChange((event, session) => {
      
      
      if (event === 'TOKEN_REFRESHED') {
        
        // Update stored session expiry on successful refresh
        if (session?.expires_at) {
          try {
            localStorage.setItem('santiyex-session-expiry', session.expires_at.toString());
          } catch (e) {
            
          }
        }
      } else if (event === 'SIGNED_OUT') {
        
        
        // Clean up session-related localStorage on sign out
        try {
          localStorage.removeItem('santiyex-session-expiry');
          localStorage.removeItem('santiyex-user-cache');
        } catch (e) {
          
        }
      } else if (event === 'SIGNED_IN') {
        
      }
    });
    
    isInitialized = true;
    isInitializing = false;
    return client;
  } catch (error) {
    
    isInitializing = false;
    throw error;
  }
};

// Create initial Supabase client
export let supabase = createSupabaseClient()!;

// Function to initialize the Supabase client safely
export const initializeSupabase = async (): Promise<void> => {
  if (isInitialized) {
    return Promise.resolve();
  }
  
  if (isInitializing && initializationPromise) {
    // Return the existing initialization promise if one exists
    return initializationPromise;
  }
  
  // Create a new initialization promise
  initializationPromise = (async () => {
    try {
      if (!supabase) {
        supabase = createSupabaseClient()!;
      }
      
      // Make a simple request to confirm the client works
      await supabase.from('people').select('count').limit(1);
    } catch (error) {
      
      // Resolve anyway to prevent blocking
    }
  })();
  
  return initializationPromise;
};

// Function to reset and recreate the Supabase client when needed
// This helps fix iOS background resume issues
export const resetSupabaseConnection = async () => {
  // Only allow one reset at a time
  if (isInitializing) {
    
    return { success: false, skipped: true };
  }
  
  try {
    // First check if we have a valid session - if so, don't reset unnecessarily
    let sessionData;
    let hasValidSession = false;
    
    try {
      const { data: currentSessionData, error } = await supabase.auth.getSession();
      sessionData = currentSessionData;
      hasValidSession = !error && currentSessionData.session !== null;
      
      if (hasValidSession) {
        
        // Test if current client works with a simple query
        const { data, error: testError } = await supabase.from('people').select('count').limit(1);
        if (!testError) {
          
          return { success: true, noReset: true };
        } else {
          
        }
      }
    } catch (sessionError) {
      
    }
    
    // Only recreate if client is already initialized and there's a connection issue
    if (isInitialized) {
      
      // Recreate the Supabase client
      const newClient = createSupabaseClient();
      
      if (newClient) {
        supabase = newClient;
      
        // Test if the new client works
        try {
          const { data, error } = await supabase.from('people').select('count').limit(1);
          
          if (error) {
            throw error;
          }
          
          
          return { success: true };
        } catch (testError) {
          
          // If we can't connect with the new client but had a valid session before,
          // try to restore the session
          if (hasValidSession && sessionData.session) {
            try {
              
              await supabase.auth.setSession({
                access_token: sessionData.session.access_token,
                refresh_token: sessionData.session.refresh_token
              });
              
            } catch (sessionError) {
              
            }
          }
          
          return { success: false, error: testError };
        }
      }
    }
    
    
    return { success: true, noReset: true };
  } catch (error) {
    
    return { success: false, error };
  }
};

// Helper to check if session is valid and not expired
export const isSessionValid = async (): Promise<boolean> => {
  try {
    // Always check with Supabase first for authoritative answer
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      
      // Clear potentially stale cache on error
      try {
        localStorage.removeItem('santiyex-session-expiry');
        localStorage.removeItem('santiyex-user-cache');
      } catch (e) {
        // Ignore localStorage errors
      }
      return false;
    }
    
    if (!data.session) {
      
      // Clear stale cache when no session exists
      try {
        localStorage.removeItem('santiyex-session-expiry');
        localStorage.removeItem('santiyex-user-cache');
      } catch (e) {
        // Ignore localStorage errors
      }
      return false;
    }
    
    // Check session expiry
    const expiresAt = data.session.expires_at;
    if (!expiresAt) {
      
      return true;
    }
    
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const isValid = expiresAt > now;
    
    
    
    // Update the stored expiry only if session is valid
    if (isValid) {
      try {
        localStorage.setItem('santiyex-session-expiry', expiresAt.toString());
      } catch (e) {
        // Ignore localStorage errors
      }
    } else {
      // Clear stale cache for expired sessions
      try {
        localStorage.removeItem('santiyex-session-expiry');
        localStorage.removeItem('santiyex-user-cache');
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    return isValid;
  } catch (error) {
    
    // Clear potentially corrupted cache on error
    try {
      localStorage.removeItem('santiyex-session-expiry');
      localStorage.removeItem('santiyex-user-cache');
    } catch (e) {
      // Ignore localStorage errors
    }
    return false;
  }
};

// Set session expiry if not already set
export const ensureSessionExpiry = async (): Promise<void> => {
  try {
    const { data } = await supabase.auth.getSession();
    
    if (!data.session) return;
    
    // If session doesn't have an expiry time, set it to 24 hours from now
    if (!data.session.expires_at) {
      const expiresAt = Math.floor(Date.now() / 1000) + 86400; // 24 hours in seconds
      
      // Store expiry in local storage as a fallback mechanism
      localStorage.setItem('santiyex-session-expiry', expiresAt.toString());
    } else {
      // Store the existing expiry in local storage for quicker checks later
      localStorage.setItem('santiyex-session-expiry', data.session.expires_at.toString());
    }
  } catch (error) {
    
  }
};

// Only do the initial test if we're in a browser environment
if (typeof window !== 'undefined') {
  // Do minimal session validation during initial load to avoid causing an infinite loop
  setTimeout(() => {
    ensureSessionExpiry().catch(err => {
      
    });
  }, 1000);
}

// Add a debugging helper function
export async function debugSupabaseConnection() {
  try {
    
    
    
    
    // Record network information if available
    let networkInfo = {};
    if (typeof navigator !== 'undefined') {
      try {
        networkInfo = {
          online: navigator.onLine,
          appVersion: navigator.appVersion,
          platform: navigator.platform,
          connection: navigator.connection ? {
            type: navigator.connection.type,
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData
          } : 'not available'
        };
        
      } catch (e) {
        
      }
    }
    
    // Try a direct fetch first to test basic connectivity
    let basicConnectivity = false;
    try {
      const startDirectFetch = Date.now();
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const response = await fetch(`${SUPABASE_URL}/rest/v1/health`, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'apikey': SUPABASE_PUBLISHABLE_KEY
          },
          cache: 'no-store',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        const directFetchTime = Date.now() - startDirectFetch;
        basicConnectivity = response.ok;
        
      } catch (e) {
        
        // Try a different endpoint as fallback
        try {
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const response = await fetch(`https://httpbin.org/status/200`, {
            method: 'GET',
            cache: 'no-store',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            
            basicConnectivity = false;
          }
        } catch (fallbackError) {
          
          basicConnectivity = false;
        }
      }
    } catch (e) {
      // Added simple log to confirm entry into catch block
       
      
      if (e instanceof Error) {
        
        
        
      } else {
        // Log the raw error object if it's not an Error instance
        try {
          
        } catch (stringifyError) {
          
          
        }
      }
    }
    
    // Now try the actual Supabase query
    const startTime = Date.now();
    const { data, error } = await supabase.from('people').select('count').limit(1);
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    if (error) {
      
      return { 
        success: false, 
        error, 
        responseTime,
        basicConnectivity,
        networkInfo,
        config: { url: SUPABASE_URL, keyPresent: !!SUPABASE_PUBLISHABLE_KEY } 
      };
    }
    
    
    return { 
      success: true, 
      data, 
      responseTime,
      basicConnectivity, 
      networkInfo,
      config: { 
        url: SUPABASE_URL, 
        keyPresent: !!SUPABASE_PUBLISHABLE_KEY,
        keyLength: SUPABASE_PUBLISHABLE_KEY.length
      } 
    };
  } catch (err) {
    
    return { 
      success: false, 
      error: err,
      networkInfo: typeof navigator !== 'undefined' ? { online: navigator.onLine } : {},
      config: { url: SUPABASE_URL, keyPresent: !!SUPABASE_PUBLISHABLE_KEY } 
    };
  }
}

// Improve quick network connectivity check to be more reliable on iOS
export const quickConnectivityCheck = async (): Promise<boolean> => {
  try {
    
    
    // Use same-origin ping endpoint to avoid CORS issues
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 seconds timeout
    
    const response = await fetch('/ping', {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      
      return true;
    }
    
    // Fallback to navigator.onLine if ping fails
    
    return navigator.onLine;
  } catch (e) {
    
    return navigator.onLine;
  }
};

// Add a comprehensive network test function with retries
export const testDirectConnectivity = async () => {
  try {
    
    
    // Use same-origin ping endpoint with retry logic
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        try {
          const response = await fetch('/ping', {
            method: 'GET',
            cache: 'no-store',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            
            return { success: true, endpoint: '/ping', attempt };
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          throw fetchError;
        }
      } catch (e) {
        
        
        // Wait before retry
        if (attempt < 1) {
          const backoffMs = 1000 * (attempt + 1);
          
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }
    }
    
    // If we get here, all attempts failed
    return { success: false, message: "Failed to connect to ping endpoint after multiple attempts" };
  } catch (e) {
    
    return { success: false, error: e };
  }
};

// Specific connectivity check using same-origin ping for iOS app resume
export const checkCloudflareConnectivity = async (timeoutMs = 5000): Promise<boolean> => {
  try {
    
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch('/ping', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        
        return true;
      } else {
        
        return false;
      }
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      return false;
    }
  } catch (e) {
    
    return false;
  }
};

// Additional helper to wait for network before critical operations
export const waitForNetworkConnectivity = async (maxRetries = 3, initialDelayMs = 1000): Promise<boolean> => {
  
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Wait with increasing backoff time
    const delayMs = initialDelayMs * (attempt + 1);
    if (attempt > 0) {
      
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    
    // Try the quick check first
    try {
      const isConnected = await quickConnectivityCheck();
      if (isConnected) {
        
        return true;
      }
    } catch (error) {
      
    }
    
    
  }
  
  // If we get here, all attempts failed
  
  return false;
};

// **NEW: iOS-friendly connectivity check for app resume scenarios**
export const testConnectivityOnResume = async (): Promise<{ success: boolean; method?: string }> => {
  
  
  // **STEP 1: Quick navigator.onLine check first**
  if (!navigator.onLine) {
    
    return { success: false, method: 'navigator.onLine' };
  }
  
  
  
  // **STEP 2: Try same-origin ping endpoint**
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    const response = await fetch('/ping', {
      method: 'HEAD', // Faster than GET
      cache: 'no-store',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      
      return { success: true, method: 'same-origin-ping' };
    }
  } catch (error) {
    
  }
  
  
  return { success: false, method: 'ping-failed' };
};

// Make the debug function available in the browser console
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).debugSupabaseConnection = debugSupabaseConnection;
  (window as any).checkSupabase = {
    url: SUPABASE_URL,
    keyPresent: !!SUPABASE_PUBLISHABLE_KEY,
    keyLength: SUPABASE_PUBLISHABLE_KEY ? SUPABASE_PUBLISHABLE_KEY.length : 0
  };
  (window as any).checkSessionValidity = isSessionValid;
  (window as any).testDirectConnectivity = testDirectConnectivity;
}